import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { InlineWidget, useCalendlyEventListener } from "react-calendly";
import {
  Calendar,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Globe,
  Link2,
  MessageSquare,
  FileText,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { MEETING_DURATIONS, type MeetingDuration } from "@/domain/intake";
import { onboardingService } from "@/services";
import type { EventDetailsResponse } from "@/services/OnboardingService";

const CALENDLY_BASE_URL = import.meta.env.VITE_CALENDLY_URL;

interface LeadContext {
  leadId: string;
  mode?: "call" | "package";
  email: string;
  name: string;
  packageId: string | null;
  timezone: string;
  meetingLength?: number;
  ndaRequired: boolean;
  emailVerified?: boolean;
  ndaSigned?: boolean;
  engagementPath?: string;
  summary: string;
}

function getLeadContext(): LeadContext | null {
  try {
    const raw = localStorage.getItem("echopeak-lead-context");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function formatDateForGoogleCalendar(isoDate: string): string {
  return new Date(isoDate)
    .toISOString()
    .replace(/[-:]/g, "")
    .replace(/\.\d{3}/, "");
}

function buildGoogleCalendarUrl(eventDetails: EventDetailsResponse): string {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: `EchoPeak Labs Discovery Call`,
    dates: `${formatDateForGoogleCalendar(eventDetails.startTime)}/${formatDateForGoogleCalendar(eventDetails.endTime)}`,
    details: "Discovery call with EchoPeak Labs to discuss your project requirements, scope, and recommended approach.",
  });
  if (eventDetails.locationLabel) {
    params.set("location", eventDetails.locationLabel);
  }
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export default function SchedulePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [leadContext, setLeadContext] = useState<LeadContext | null>(null);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [meetingLength, setMeetingLength] = useState<MeetingDuration>(45);
  const [widgetReady, setWidgetReady] = useState(false);
  const [eventDetails, setEventDetails] =
    useState<EventDetailsResponse | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);

  const calendlySlug =
    MEETING_DURATIONS.find((d) => d.duration === meetingLength)?.calendlySlug ??
    "consultation-45-min";
  const calendlyUrl = `${CALENDLY_BASE_URL}/${calendlySlug}`;

  const handleMeetingLengthChange = (duration: MeetingDuration) => {
    setMeetingLength(duration);
    setWidgetReady(false);
  };

  const fetchEventDetails = useCallback(async (eventUri: string) => {
    setLoadingDetails(true);
    try {
      const details = await onboardingService.getEventDetails(eventUri);
      setEventDetails(details);
    } catch (err) {
      console.error("Failed to fetch event details:", err);
    } finally {
      setLoadingDetails(false);
    }
  }, []);

  useEffect(() => {
    const ctx = getLeadContext();
    if (!ctx || !ctx.emailVerified) {
      navigate("/verify/email");
      return;
    }
    if (ctx.ndaRequired && !ctx.ndaSigned && ctx.mode !== "call") {
      navigate("/verify/nda");
      return;
    }
    setLeadContext(ctx);
    if (ctx.meetingLength)
      setMeetingLength(ctx.meetingLength as MeetingDuration);
  }, [navigate]);

  useEffect(() => {
    if (
      import.meta.env.VITE_E2E_MODE === "true" &&
      searchParams.get("e2eBooked") === "true"
    ) {
      setBookingComplete(true);
      fetchEventDetails("e2e-calendly-event");
    }
  }, [searchParams, fetchEventDetails]);

  useCalendlyEventListener({
    onProfilePageViewed: () => setWidgetReady(true),
    onEventTypeViewed: () => setWidgetReady(true),
    onEventScheduled: (e) => {
      const payload = e.data.payload;
      const eventUri = (payload as Record<string, { uri?: string }>)?.event
        ?.uri;

      if (leadContext) {
        localStorage.setItem(
          "echopeak-lead-context",
          JSON.stringify({
            ...leadContext,
            callBookedAt: new Date().toISOString(),
            meetingLength,
            calendlyEventUri: eventUri,
            calendlyInviteeUri: (payload as Record<string, { uri?: string }>)
              ?.invitee?.uri,
          }),
        );
      }

      if (eventUri) {
        fetchEventDetails(eventUri);
      }

      setBookingComplete(true);
    },
  });

  if (!leadContext) return null;

  const locationLabel = eventDetails?.locationLabel ?? "Meeting";

  if (bookingComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-24">
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute inset-0 grid-background opacity-40" />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-2xl w-full relative z-10"
        >
          <Card className="glass-card border-gradient text-center">
            <CardContent className="pt-12 pb-8 px-8">
              <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-10 w-10 text-emerald-400" />
              </div>
              <h1 className="font-display text-3xl md:text-4xl font-bold mb-4">
                Your discovery call is booked!
              </h1>

              <Card className="bg-primary/10 border-primary/30 mb-6">
                <CardContent className="p-4">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Clock className="h-4 w-4 text-primary" />
                    <span className="font-semibold">
                      {meetingLength} minute discovery call
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Globe className="h-3.5 w-3.5" />
                    <span>{leadContext.timezone}</span>
                  </div>
                </CardContent>
              </Card>

              <div className="flex items-center justify-center gap-2 mb-6">
                <Link2 className="h-4 w-4 text-blue-400" />
                <span className="text-sm text-muted-foreground">
                  {loadingDetails ? (
                    "Loading meeting details..."
                  ) : (
                    <>
                      {locationLabel} link will be sent to{" "}
                      <span className="font-medium text-foreground">
                        {leadContext.email}
                      </span>
                    </>
                  )}
                </span>
              </div>

              <div className="text-left bg-card/50 rounded-lg p-6 mb-8">
                <h3 className="font-semibold mb-4">What happens next</h3>
                <ul className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex gap-3">
                    <span className="text-primary font-semibold shrink-0">
                      Now:
                    </span>
                    Calendar invite and meeting link sent to your email
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary font-semibold shrink-0">
                      Before call:
                    </span>
                    Prepare any non-sensitive project details or questions
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary font-semibold shrink-0">
                      During call:
                    </span>
                    We'll discuss requirements, scope, and recommended approach
                  </li>
                  <li className="flex gap-3">
                    <span className="text-primary font-semibold shrink-0">
                      After call:
                    </span>
                    If proceeding, you'll complete contract details for MSA/SOW
                  </li>
                </ul>
              </div>

              {leadContext.ndaSigned && (
                <div className="flex items-center justify-center gap-2 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20 mb-6">
                  <Badge
                    variant="outline"
                    className="text-emerald-400 border-emerald-500/30"
                  >
                    NDA Signed
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    You can share sensitive details during the call
                  </span>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                <Button
                  variant="outline"
                  onClick={() => setCalendarDialogOpen(true)}
                  disabled={!eventDetails}
                >
                  <Calendar className="h-4 w-4 mr-2" />
                  Add to Calendar
                </Button>
                <Button onClick={() => navigate("/")}>Return to Home</Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <Dialog open={calendarDialogOpen} onOpenChange={setCalendarDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add to Calendar</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              {eventDetails && (
                <Button
                  variant="outline"
                  className="w-full justify-start gap-3 h-auto py-3"
                  onClick={() => {
                    window.open(
                      buildGoogleCalendarUrl(eventDetails),
                      "_blank",
                    );
                    setCalendarDialogOpen(false);
                  }}
                >
                  <Calendar className="h-5 w-5 text-blue-500 shrink-0" />
                  <div className="text-left">
                    <p className="font-medium">Google Calendar</p>
                    <p className="text-xs text-muted-foreground">
                      Opens in a new tab
                    </p>
                  </div>
                  <ExternalLink className="h-4 w-4 ml-auto text-muted-foreground" />
                </Button>
              )}
              <Button
                variant="outline"
                className="w-full justify-start gap-3 h-auto py-3"
                disabled
              >
                <Calendar className="h-5 w-5 text-muted-foreground shrink-0" />
                <div className="text-left">
                  <p className="font-medium text-muted-foreground">
                    Apple Calendar
                  </p>
                  <p className="text-xs text-muted-foreground">Coming soon</p>
                </div>
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start gap-3 h-auto py-3"
                disabled
              >
                <Calendar className="h-5 w-5 text-muted-foreground shrink-0" />
                <div className="text-left">
                  <p className="font-medium text-muted-foreground">
                    Outlook Calendar
                  </p>
                  <p className="text-xs text-muted-foreground">Coming soon</p>
                </div>
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 md:py-16 px-6">
      <div className="absolute inset-0 hero-gradient" />
      <div className="absolute inset-0 grid-background opacity-40" />

      <div className="container mx-auto relative z-10 max-w-4xl">
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate("/engage")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to options
          </Button>
          {leadContext.mode === "package" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                localStorage.setItem(
                  "echopeak-lead-context",
                  JSON.stringify({
                    ...leadContext,
                    engagementPath: "questionnaire",
                  }),
                );
                navigate("/questionnaire");
              }}
            >
              <FileText className="h-4 w-4 mr-2" />
              Switch to questionnaire
            </Button>
          )}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
            <Calendar className="h-8 w-8 text-primary" />
          </div>
          <h1 className="font-display text-3xl font-bold mb-2">
            Schedule Your Discovery Call
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Choose a time that works best for you. A meeting link will be sent
            to {leadContext.email}.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <Card className="glass-card border-gradient overflow-hidden">
              <CardContent className="p-0 relative" style={{ minHeight: 580 }}>
                {!widgetReady && (
                  <div className="absolute inset-0 flex items-center justify-center z-10 bg-card/80">
                    <div className="flex flex-col items-center gap-3">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        Loading calendar...
                      </p>
                    </div>
                  </div>
                )}
                <div
                  className={cn(
                    "transition-opacity duration-300",
                    widgetReady ? "opacity-100" : "opacity-0",
                  )}
                >
                  <InlineWidget
                    key={meetingLength}
                    url={calendlyUrl}
                    prefill={{
                      name: leadContext.name,
                      email: leadContext.email,
                      customAnswers: {
                        ...(leadContext.packageId
                          ? { a2: leadContext.packageId }
                          : {}),
                        ...(leadContext.summary
                          ? { a3: leadContext.summary.slice(0, 200) }
                          : {}),
                      },
                    }}
                    styles={{ height: "580px" }}
                    pageSettings={{
                      backgroundColor: "0a0a0a",
                      primaryColor: "7c3aed",
                      textColor: "e5e5e5",
                      hideEventTypeDetails: false,
                      hideLandingPageDetails: false,
                      hideGdprBanner: true,
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          <div>
            <Card className="glass-card border-gradient">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4">Booking Details</h3>
                <div className="space-y-4 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">
                      Meeting length
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {MEETING_DURATIONS.map(({ duration, label }) => (
                        <button
                          key={duration}
                          type="button"
                          onClick={() => handleMeetingLengthChange(duration)}
                          className={cn(
                            "py-2 px-1 rounded-lg border text-xs font-medium transition-all text-center",
                            meetingLength === duration
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border hover:border-primary/50",
                          )}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">{leadContext.name}</p>
                      <p className="text-muted-foreground">
                        {leadContext.email}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
